import { cn } from "@/lib/utils";
import React from "react";
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../ui/tooltip";

type Props = {
  url?: string | null;
  name?: string;
  className?: string;
  imgClassName?: string;
  avatarSize?: number;
};

function Avatar({
  url,
  name = "",
  className,
  imgClassName,
  avatarSize = 20,
}: Props) {
  const imgRef = React.useRef<HTMLImageElement>(null);
  const fontSize = avatarSize * (2 / 5);

  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center border-1 border-accent-foreground bg-primary font-bold relative",
        className,
      )}
      style={{
        width: `${avatarSize}px`,
        height: `${avatarSize}px`,
        fontSize: `${fontSize}px`,
      }}
    >
      {name
        .split(" ")
        .slice(0, 2)
        .map((word) => word.charAt(0).toLocaleUpperCase())
        .join("")}

      {url && (
        <img
          ref={imgRef}
          src={url}
          alt=""
          onError={() => {
            if (imgRef.current) imgRef.current.style.display = "none";
          }}
          className={cn(
            "rounded-full object-cover border-1 border-accent-foreground block absolute top-[-1px] left-0",
            className,
            imgClassName,
          )}
          style={{ width: `${avatarSize}px`, height: `${avatarSize}px` }}
        />
      )}
    </div>
  );
}

export function AvatarRow({
  items,
  avatarSize = 20,
  avatarLimit = 3,
}: {
  items: {
    text: string;
    url?: string | null;
    tooltipContent: React.ReactNode;
  }[];
  avatarSize?: number;
  avatarLimit?: number;
}) {
  const overlapOffset = avatarSize * (2 / 3);
  const renderedCount =
    items.length > avatarLimit ? avatarLimit + 1 : items.length;
  const containerWidth = avatarSize + overlapOffset * (renderedCount - 1);
  const fontSize = avatarSize * (2 / 5);

  return (
    <div
      className="relative"
      style={{ width: `${containerWidth}px`, height: `${avatarSize}px` }}
    >
      {items.slice(0, avatarLimit)?.map((item, index) => {
        return (
          <div
            key={index}
            className="absolute top-1/2 -translate-y-1/2"
            style={{
              width: `${avatarSize}px`,
              height: `${avatarSize}px`,
              left: `${index * overlapOffset}px`,
              zIndex: index,
            }}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="cursor-pointer">
                  <Avatar
                    url={item.url}
                    name={item.text}
                    avatarSize={avatarSize}
                  />
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  collisionPadding={34}
                  className="bg-accordion"
                >
                  {item.tooltipContent}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      })}

      {items.length > avatarLimit && (
        <div
          className="flex items-center justify-center bg-tertiary text-secondary-foreground absolute rounded-full font-bold top-1/2 -translate-y-1/2"
          style={{
            width: `${avatarSize}px`,
            height: `${avatarSize}px`,
            left: `${avatarLimit * overlapOffset}px`,
            zIndex: avatarLimit,
            fontSize: `${fontSize}px`,
          }}
        >
          +{items.length - avatarLimit}
        </div>
      )}
    </div>
  );
}

export default Avatar;
